import type { ISchedulerTask } from '../../interfaces/IScheduler.js';

// ---------------------------------------------------------------------------
// Where a task sits in the plan: the summary tasks above it, outermost first.
// The drawer header (web) and the task sheet header (mobile) read it as a trail — group › summary › … › this task — so
// a sub-task three levels down says what it belongs to instead of floating on
// its own name.
// ---------------------------------------------------------------------------

/** One step of the trail: enough to name it, and to go to it. */
export type IAncestor = Pick<ISchedulerTask, 'id' | 'name'>;

/**
 * Every ancestor of `task`, outermost first. Empty for a top-level task.
 *
 * A plan imported with a broken parent chain could point back at itself; the
 * walk keeps the ids it has seen and stops there rather than spinning.
 */
export function ancestors(
  tasks: readonly ISchedulerTask[],
  task: Pick<ISchedulerTask, 'parentId'>,
): IAncestor[] {
  const byId = new Map(tasks.map((row) => [row.id, row] as const));
  const trail: IAncestor[] = [];
  const seen = new Set<string>();
  let parentId = task.parentId;
  while (parentId != null && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) break;
    trail.push({ id: parent.id, name: parent.name });
    parentId = parent.parentId;
  }
  return trail.reverse();
}
